import { useRef, useState } from "react";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, useAssignees } from "@/lib/crm";
import { StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TONE = { nuovo: "green", duplicato: "amber", errore: "red" };

const downloadTemplate = async () => {
  try {
    const r = await api.get("/crm/companies/import/template", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = "modello_import_clienti.xlsx"; a.click(); URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errorMessage(e)); }
};

const PreviewTable = ({ rows }) => (
  <div className="max-h-72 overflow-auto rounded-control border border-line" data-testid="import-preview">
    <table className="w-full text-left text-sm">
      <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2">Riga</th><th className="px-3 py-2">Ragione sociale</th><th className="px-3 py-2">P.IVA</th><th className="px-3 py-2">Referente</th><th className="px-3 py-2">Esito</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.row} className="border-t border-slate-100" data-testid={`import-row-${r.row}`}>
          <td className="px-3 py-2 text-slate-500">{r.row}</td><td className="px-3 py-2 font-medium">{r.name || "—"}</td>
          <td className="px-3 py-2">{r.vat_number || "—"}</td><td className="px-3 py-2">{r.contact || "—"}</td>
          <td className="px-3 py-2"><StatusBadge tone={TONE[r.status]}>{r.status}</StatusBadge>{r.message && <span className="ml-2 text-xs text-slate-500">{r.message}</span>}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const ImportCompaniesDialog = ({ open, onOpenChange, onDone }) => {
  const { me } = useAuth();
  const users = useAssignees();
  const ref = useRef(null);
  const [prev, setPrev] = useState(null), [owner, setOwner] = useState(""), [busy, setBusy] = useState(false);
  const reset = (o) => { if (!o) { setPrev(null); setOwner(""); } onOpenChange(o); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { setPrev((await api.post("/crm/companies/import/preview", fd)).data); setOwner((o) => o || me?.id || ""); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/crm/companies/import/commit", { owner_id: owner, rows: prev.rows.filter((x) => x.status === "nuovo").map((x) => x.data) });
      toast.success(`${r.data.created} clienti importati${r.data.skipped.length ? `, ${r.data.skipped.length} saltati` : ""}`);
      reset(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const n = prev?.counts || {};
  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-3xl" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importa clienti da Excel</DialogTitle>
          <DialogDescription>Usa il modello (.xlsx o .csv). I clienti già presenti in ON! (stessa P.IVA, codice fiscale o ragione sociale) vengono saltati.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={downloadTemplate} data-testid="import-template-btn"><Download className="h-4 w-4" />Scarica modello</Button>
          <Button variant="outline" className="gap-2" disabled={busy} onClick={() => ref.current?.click()} data-testid="import-upload-btn"><Upload className="h-4 w-4" />{busy && !prev ? "Analisi…" : "Carica file"}</Button>
          <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
        </div>
        {prev ? <>
          <p className="text-sm" data-testid="import-counts"><b className="text-emerald-700">{n.nuovo} nuovi</b> · <span className="text-amber-700">{n.duplicato} già presenti</span> · <span className="text-red-600">{n.errore} con errori</span></p>
          <PreviewTable rows={prev.rows} />
          <SelectField label="Responsabile dei clienti importati" value={owner} onChange={setOwner} options={users.map((u) => ({ key: u.id, label: u.name }))} testId="import-owner" />
        </> : <div className="flex flex-col items-center rounded-control border border-dashed border-line py-10 text-sm text-slate-500"><FileSpreadsheet className="mb-2 h-8 w-8 text-slate-300" />Carica il file per vedere l'anteprima</div>}
        <DialogFooter><Button variant="outline" onClick={() => reset(false)} data-testid="import-cancel-btn">Annulla</Button>
          <Button onClick={commit} disabled={busy || !prev || !n.nuovo || !owner} data-testid="import-commit-btn">Importa {n.nuovo || 0} clienti</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
